import moment from "moment";
import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  useLazyViewSolarPlantStatisticsAdminQuery,
  useLazyViewSolarPlantStatusesQuery,
  useLazyViewTotalPlantGenerationQuery,
} from "../../redux/api/dashboard/dashboardAPI";
import {
  resetSitesListFilters,
  setSelectPlantStatus,
} from "../../redux/features/sites/sites-slice";
import SiteStatusCount from "../../shared/components/site-status-count/site-status-count";
import SolarInfoStatisticCard from "../../shared/components/solar-info-statistic-card/solar-info-statistic-card";
import IViewCustomerSolarPlantDetailsResponseDTO from "../../shared/oversight-core/dtos/response-dtos/view-customer-solar-plant-details-response-dto";
import ViewSolarPlantStatusesResponseDTO from "../../shared/oversight-core/dtos/response-dtos/view-solar-plant-statuses-response-dto";
import { IViewTotalSolarPlantEnergyResponseDTO } from "../../shared/oversight-core/dtos/response-dtos/view-total-solar-plant-energy-response-dto";
import { EDateTypes } from "../../shared/oversight-core/enums/date-types";
import { EPlantStatus } from "../../shared/oversight-core/enums/plant-status";
import { AppRoute } from "../../shared/oversight-core/interfaces/app-routes";
import AppDropDown from "../../shared/oversight-core/ui-elements/app-dropdown/app-drop-down";
import BarChart from "../../shared/oversight-core/ui-elements/bar-chart/bar-chart";
import ButtonWithBadge from "../../shared/oversight-core/ui-elements/buttons/button-with-badge/button-with-badge";
import SpinnerModal from "../../shared/oversight-core/ui-elements/spinner/spinner";
import Helper from "../../shared/oversight-core/utils/helpers";
import { useIsGuest } from "../../shared/utils/org-roles";
import DashboardFilters from "./components/dashboard-filters/dashboard-filters";
import ExportFleetReportModal from "./components/export-fleet-report-modal/export-fleet-report-modal";
import styles from "./dashboard.module.scss";

interface ChartData {
  values: number[];
  labels: string[];
}

const defaultChartDataValues = {
  values: [],
  labels: [],
};

const AdminDashboard = () => {
  const isGuest = useIsGuest();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [data, setData] = useState<IViewCustomerSolarPlantDetailsResponseDTO>();
  const [solarPowerData, setSolarPowerData] = useState<ChartData>({
    ...defaultChartDataValues,
  });
  const [solarPlantStatuses, setSolarPlantStatuses] =
    useState<ViewSolarPlantStatusesResponseDTO>();

  const [showSolarFilterSidePanel, setShowSolarFilterSidePanel] =
    useState(false);
  const [showExportFleetReportModal, setShowExportFleetReportModal] =
    useState(false);
  const [filterBadgeValue, setFilterBadgeValue] = useState(0);

  const [filterStartDate, setFilterStartDate] = useState<Date>();
  const [filterEndDate, setFilterEndDate] = useState<Date>();
  const [filterDateType, setFilterDateType] = useState<EDateTypes>(
    EDateTypes.DAILY,
  );

  const [
    triggerSolarPlantStatistics,
    { isFetching: isFetchingSolarPlantStatistics },
  ] = useLazyViewSolarPlantStatisticsAdminQuery();
  const [
    triggerTotalPlantGeneration,
    { isFetching: isFetchingTotalPlantGeneration },
  ] = useLazyViewTotalPlantGenerationQuery();
  const [
    triggerSolarPlantStatuses,
    { isFetching: isFetchingSolarPlantStatuses },
  ] = useLazyViewSolarPlantStatusesQuery();

  useEffect(() => {
    triggerSolarPlantStatistics()
      .unwrap()
      .then((res: IViewCustomerSolarPlantDetailsResponseDTO) => {
        setData(res);
      })
      .catch(() => {
        setData(undefined);
      });
  }, [triggerSolarPlantStatistics]);

  useEffect(() => {
    triggerSolarPlantStatuses({ extended: false })
      .unwrap()
      .then((res: ViewSolarPlantStatusesResponseDTO) => {
        setSolarPlantStatuses(res);
      })
      .catch(() => {
        setSolarPlantStatuses(undefined);
      });
  }, [triggerSolarPlantStatuses]);

  useEffect(() => {
    triggerTotalPlantGeneration({
      startDate: filterStartDate
        ? moment(filterStartDate).format("YYYY-MM-DD")
        : undefined,
      endDate: filterEndDate
        ? moment(filterEndDate).format("YYYY-MM-DD")
        : undefined,
      viewType: filterDateType,
    })
      .unwrap()
      .then((res: IViewTotalSolarPlantEnergyResponseDTO) => {
        const power = res.solarGeneration;

        setSolarPowerData({
          values: power.map((p) => p.energy / 1000),
          labels: power.map((e) =>
            moment(e.date)
              .utcOffset("+05:30")
              .format(
                filterDateType === EDateTypes.DAILY
                  ? "MM-DD"
                  : filterDateType === EDateTypes.MONTHLY
                    ? "MM-YYYY"
                    : "YYYY",
              ),
          ),
        });
      })
      .catch(() => {
        setSolarPowerData({ ...defaultChartDataValues });
      });
  }, [
    triggerTotalPlantGeneration,
    filterStartDate,
    filterEndDate,
    filterDateType,
  ]);

  const onFilter = (
    startDate: Date | undefined,
    endDate: Date | undefined,
    selectedDateType: EDateTypes,
  ) => {
    let adjustedStartDate = startDate;
    let adjustedEndDate = endDate;

    if (adjustedStartDate && adjustedEndDate) {
      if (selectedDateType === EDateTypes.MONTHLY) {
        adjustedStartDate = moment(adjustedStartDate).startOf("month").toDate();
        adjustedEndDate = moment(adjustedEndDate).endOf("month").toDate();
      } else if (selectedDateType === EDateTypes.YEARLY) {
        adjustedStartDate = moment(adjustedStartDate).startOf("year").toDate();
        adjustedEndDate = moment(adjustedEndDate).endOf("year").toDate();
      }

      const now = new Date();
      if (adjustedEndDate > now) {
        adjustedEndDate = now;
      }
    }

    setFilterStartDate(adjustedStartDate);
    setFilterEndDate(adjustedEndDate);
    setFilterDateType(selectedDateType);
    setFilterBadgeValue(1);
    setShowSolarFilterSidePanel(false);
  };

  const onClearAll = () => {
    setFilterStartDate(undefined);
    setFilterEndDate(undefined);
    setFilterDateType(EDateTypes.DAILY);
    setFilterBadgeValue(0);
    setShowSolarFilterSidePanel(false);
  };

  const formatGeneration = (
    totalGeneration: number,
  ): { value: number; unit: string } => {
    const absValue = Math.abs(totalGeneration);
    const digits = Math.floor(Math.log10(absValue)) + 1;

    if (digits <= 8) {
      // Convert to kW
      return {
        value: Number(Helper.roundTo2(totalGeneration / 1_000)),
        unit: "kWh",
      };
    } else {
      // Convert to MW
      return {
        value: Number(Helper.roundTo2(totalGeneration / 1_000_000)),
        unit: "MWh",
      };
    }
  };

  return (
    <>
      <div className="container-white px-3">
        <Row className="gy-4">
          <Col className="col-12 col-lg-6 col-xl-3">
            <SolarInfoStatisticCard
              title="Total Sites"
              value={data?.numberOfPlant || 0}
              isLoading={isFetchingSolarPlantStatistics}
            />
          </Col>
          <Col className="col-12 col-lg-6 col-xl-3">
            <SolarInfoStatisticCard
              title="Total Generation"
              value={formatGeneration(data?.totalGeneration ?? 0).value}
              unit={formatGeneration(data?.totalGeneration ?? 0).unit}
              isLoading={isFetchingSolarPlantStatistics}
            />
          </Col>
          <Col className="col-12 col-lg-6 col-xl-3">
            <SolarInfoStatisticCard
              title="Monthly Total"
              value={Number(
                Helper.roundTo2((data?.totalMonthlyGeneration ?? 0) / 1000),
              )}
              unit="kWh"
              isLoading={isFetchingSolarPlantStatistics}
            />
          </Col>
          <Col className="col-12 col-lg-6 col-xl-3">
            <SolarInfoStatisticCard
              title="Today Total Generation"
              value={Number(
                Helper.roundTo2((data?.totalDailyGeneration ?? 0) / 1000),
              )}
              unit="kWh"
              isLoading={isFetchingSolarPlantStatistics}
            />
          </Col>
          <Col className="col-12 col-lg-6 col-xl-3">
            <SolarInfoStatisticCard
              title="Largest Individual Site"
              value={Number(
                Helper.roundTo2((data?.largestSiteCapacity ?? 0) / 1000),
              )}
              unit="kWh"
              isLoading={isFetchingSolarPlantStatistics}
            />
          </Col>
        </Row>
      </div>
      <div className={`${styles.siteStatusCardSContainer} p-2 mt-4`}>
        <Row className="row-cols-1 row-cols-sm-2 row-cols-xl-4 g-2">
          <Col>
            <SiteStatusCount
              title="All Sites"
              count={solarPlantStatuses?.numberOfTotalPlants || 0}
              onClick={() => {
                navigate(AppRoute.ADMIN_SITES);
                dispatch(resetSitesListFilters());
                dispatch(setSelectPlantStatus(undefined));
              }}
              icon="solar_power"
              iconColor="#469CD9"
              isLoading={isFetchingSolarPlantStatuses}
            />
          </Col>
          <Col>
            <SiteStatusCount
              title="Active Sites"
              count={solarPlantStatuses?.numberOfOnlinePlants || 0}
              onClick={() => {
                navigate(AppRoute.ADMIN_SITES);
                dispatch(resetSitesListFilters());
                dispatch(setSelectPlantStatus(EPlantStatus.ACTIVE));
              }}
              icon="fiber_manual_record"
              iconColor="#29CC39"
              isLoading={isFetchingSolarPlantStatuses}
            />
          </Col>
          <Col>
            <SiteStatusCount
              title="Offline Sites"
              count={solarPlantStatuses?.numberOfOfflinePlants || 0}
              onClick={() => {
                navigate(AppRoute.ADMIN_SITES);
                dispatch(resetSitesListFilters());
                dispatch(setSelectPlantStatus(EPlantStatus.OFFLINE));
              }}
              icon="wifi_off"
              iconColor="#7D7B86"
              isLoading={isFetchingSolarPlantStatuses}
            />
          </Col>
          {/* A monitoring guest is not shown alerts; the backend sends no count. */}
          {!isGuest && (
            <Col>
              <SiteStatusCount
                title="Errors"
                count={solarPlantStatuses?.numberOfAlerts || 0}
                onClick={() => {
                  navigate(AppRoute.ADMIN_SITES);
                  dispatch(resetSitesListFilters());
                  dispatch(setSelectPlantStatus(EPlantStatus.FAULT));
                }}
                icon="emergency_home"
                iconColor="#E45E5E"
                isLoading={isFetchingSolarPlantStatuses}
              />
            </Col>
          )}
        </Row>
      </div>
      <div className="container-white position-relative mt-4">
        <Row className="justify-content-end mb-3 mx-0 align-items-center">
          <Col className="col-auto pe-0">
            <AppDropDown
              items={[
                {
                  text: "Export",
                  onClick: () => setShowExportFleetReportModal(true),
                },
              ]}
              size="md"
              menuPlacement="below"
            />
          </Col>
          <Col className="col-auto">
            <ButtonWithBadge
              text="Filter"
              icon="filter_alt"
              onClick={() => {
                setShowSolarFilterSidePanel(true);
              }}
              badgeValue={filterBadgeValue}
            />
          </Col>
        </Row>
        <BarChart
          data1={solarPowerData.values}
          dataLabel1="Solar Power Generation of All Sites"
          labels={solarPowerData.labels}
          borderColor1="#D08700"
          backgroundColor1="#D08700"
          yAxesUnit="kWh"
          xAxisLabel={
            filterDateType === EDateTypes.MONTHLY
              ? "Month"
              : filterDateType === EDateTypes.YEARLY
                ? "Year"
                : "Day"
          }
          isDisplayLegend
          isChartDataAvailable={solarPowerData.values.length > 0}
        />
        <SpinnerModal show={isFetchingTotalPlantGeneration} />
      </div>
      <DashboardFilters
        show={showSolarFilterSidePanel}
        onClose={() => setShowSolarFilterSidePanel(false)}
        onFilter={onFilter}
        onClearAll={onClearAll}
        defaultDateType={filterDateType}
        defaultStartDate={filterStartDate}
        defaultEndDate={filterEndDate}
      />
      <ExportFleetReportModal
        show={showExportFleetReportModal}
        onClose={() => setShowExportFleetReportModal(false)}
      />
    </>
  );
};

export default AdminDashboard;
